import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth.jsx";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import { GraduationCap, Languages } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const { t, lang, setLang } = useI18n();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      nav(`/${u.role}`);
      toast.success(`${t("welcome")}, ${u.name}`);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || err.message,
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid-bg grain-bg grid md:grid-cols-2">
      {/* Left: form */}
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="mb-8 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="grid h-10 w-10 place-items-center rounded-2xl bg-primary text-primary-foreground">
                <GraduationCap className="h-5 w-5" />
              </div>
              <span className="font-display text-xl font-bold">
                {t("appName")}
              </span>
            </Link>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLang(lang === "en" ? "ms" : "en")}
              data-testid="lang-toggle"
              className="gap-2"
            >
              <Languages className="h-4 w-4" />
              {t("switchLanguage")}
            </Button>
          </div>

          <h1 className="font-display text-4xl font-extrabold tracking-tight sm:text-5xl">
            {t("welcome")}.
          </h1>
          <p className="mt-2 text-muted-foreground">{t("tagline")}</p>

          <Card className="mt-8 rounded-2xl border-border/70 shadow-sm">
            <CardContent className="p-6">
              <form onSubmit={onSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">{t("email")}</Label>
                  <Input
                    id="email"
                    data-testid="login-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-12"
                    placeholder="you@school.my"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">{t("password")}</Label>
                  <Input
                    id="password"
                    data-testid="login-password"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-12"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={busy}
                  data-testid="login-submit"
                  className="h-12 w-full rounded-full text-base"
                >
                  {busy ? "…" : t("login")}
                </Button>
              </form>
              <div className="mt-4 text-center text-sm text-muted-foreground">
                {lang === "en"
                  ? "New parent here? "
                  : "Ibu bapa baru? "}
                <Link
                  to="/register"
                  className="font-semibold text-primary hover:underline"
                  data-testid="go-register"
                >
                  {t("register")}
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Right: art */}
      <div className="relative hidden overflow-hidden md:block">
        <img
          src="https://images.pexels.com/photos/37811262/pexels-photo-37811262.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="students studying"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-primary/40 mix-blend-multiply" />
        <div className="absolute bottom-10 left-10 right-10 text-primary-foreground">
          <div className="max-w-md rounded-2xl bg-black/40 p-6 backdrop-blur">
            <p className="font-display text-2xl font-bold leading-tight">
              {lang === "en"
                ? "Every present student is a promise kept."
                : "Setiap murid yang hadir adalah janji yang ditepati."}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
